import React from 'react';
import ScrollingSection from '../../../../../components/scrolling-section';
import { useIsIntersecting } from '../../../../../helpers/use-is-intersecting';
import { WeakType } from '../../../../../typescript-migration';
import {
  useEntitiesRowQuery,
  useTrainersRowQuery,
} from '../../../../../client/query/entities';
import { useCompletionsQuery } from '../../../../../client/query/completions';

interface IOwnProps {
  index: number;
  queryObj: WeakType;
  title: string;
  translatedTitle: string;
  href?: string;
  completionsMinMax?: { min: number; max: number };
}

export interface IProps extends IOwnProps {}

export const buildDefaultRoute = (title: string): string =>
  `/query/${encodeURIComponent(title)}`;

export const QueryRow: React.FC<IProps> = props => {
  const {
    title,
    translatedTitle,
    queryObj = {},
    href,
    index,
    completionsMinMax,
  } = props;

  const defaultRoute = buildDefaultRoute(title);
  const isEntityRow = queryObj.route === '/entities';
  const route = href || defaultRoute;

  const {
    data: completions,
    isLoading: isCompletionsLoading,
  } = useCompletionsQuery();

  const [ref, isIntersecting] = useIsIntersecting({
    intersectWithinSeconds: 120,
  });

  const entities = useEntitiesRowQuery(queryObj.params, {
    enabled: isIntersecting && isEntityRow,
  });

  const trainers = useTrainersRowQuery(queryObj.params, {
    enabled: isIntersecting && !isEntityRow,
  });

  const { data = [], isFetching, isError, refetch } = isEntityRow
    ? entities
    : trainers;

  const completionsCount = completions ? Object.keys(completions).length : 0;

  const skipMinMax =
    completionsMinMax?.min === undefined ||
    completionsMinMax?.max === undefined;

  const withinMinMax = skipMinMax
    ? true
    : completionsMinMax?.min <= completionsCount &&
      completionsMinMax?.max >= completionsCount;

  if (!isCompletionsLoading && !withinMinMax) {
    return null;
  }

  const listData = isEntityRow ? data[0] : data;
  return Object.keys(queryObj).length === 0 ? (
    <></>
  ) : (
    <ScrollingSection
      reload={refetch}
      ref={ref}
      showCategory
      isLoading={isFetching}
      title={translatedTitle}
      route={route}
      listData={listData}
      isError={isError}
      analytics={{
        engagementData: {
          rowNumber: index,
        },
      }}
    />
  );
};

export default QueryRow;
